import type { AnalyticsSummary } from '../../cashier-analytics/model/types'
import type { SalesPointRegister } from './types'

/** Кнопка отбора по кассе: номер `источник:касса` и подпись. */
export interface RegisterChoice { id: string; label: string }

export const registerIdOf = (resourceId: string, registerKey: string) => `${resourceId}:${registerKey}`

/** Кнопки по кассам магазина; у касс с одинаковым названием в подписи добавляется источник. */
export function registerChoices(registers: readonly SalesPointRegister[]): RegisterChoice[] {
  const named = registers.map((item) => ({ item, name: item.registerName.trim() === '' ? 'Касса без названия' : item.registerName }))
  const repeated = new Set(named.filter((one, at) => named.findIndex((other) => other.name === one.name) !== at).map((one) => one.name))
  return named.map(({ item, name }) => ({ id: registerIdOf(item.resourceId, item.registerKey), label: repeated.has(name) ? `${name} · ${item.resourceName}` : name }))
}

/** Выбранные кассы: пустой список — отбора нет, активны все. */
export const activeRegisters = (all: readonly RegisterChoice[], chosen: readonly string[]): ReadonlySet<string> =>
  new Set(chosen.length === 0 ? all.map((item) => item.id) : chosen)

/**
 * Нажатие кнопки кассы меняет только её саму: включённая выключается, выключенная включается. Хотя бы одна касса остаётся
 * включённой (последнюю выключить нельзя); когда включены все — отбора нет (пустой список).
 */
export function toggleRegister(all: readonly RegisterChoice[], chosen: readonly string[], id: string): string[] {
  const active = activeRegisters(all, chosen)
  const next = new Set(active)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  if (next.size === 0) return [...chosen]
  return next.size === all.length ? [] : all.map((item) => item.id).filter((item) => next.has(item))
}

/** Итоги только по выбранным кассам: дни остальных касс отбрасываются, источники и недатированные чеки остаются как есть. */
export function summaryOfRegisters(summary: AnalyticsSummary | undefined, chosen: readonly string[]): AnalyticsSummary | undefined {
  if (summary === undefined || chosen.length === 0) return summary
  const active = new Set(chosen)
  return { ...summary, days: summary.days.filter((day) => active.has(registerIdOf(day.resourceId, day.registerKey))) }
}
